import React from "react";
import { GripVertical, Image, Star } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { PresignedImageWithPlaceholder } from "@/components/common/PresignedImageWithPlaceholder";
import { SortableList } from "@/components/generic/SortableList";
import { cn } from "@/lib/utils";
import type { Amenity } from "@/types/amenity";
import {
  useHomeAmenities,
  useReorderAmenities,
  useUpdateAmenity,
} from "@/components/pages/amenities/hooks/amenities.hooks";
import type { AmenityAccess } from "@/components/pages/amenities/utils/amenityAccess";
import {
  AMENITY_HOME_STATE_KEYS,
  getAmenityHomeStates,
  getHomeStarLabelKey,
  type AmenityHomeState,
} from "@/components/pages/amenities/utils/amenityHomeRow";

interface HomeAmenitiesSectionProps {
  hotelId?: string;
  access: AmenityAccess;
}

const stopInsideSortable = (e: React.SyntheticEvent) => e.stopPropagation();

/**
 * Starred amenities in guest home order, above the table. Dragging saves the whole list with
 * POST /amenities/reorder; without the edit right the order is shown read-only.
 */
export const HomeAmenitiesSection: React.FC<HomeAmenitiesSectionProps> = ({
  hotelId,
  access,
}) => {
  const { t } = useTranslation();
  const { data, isLoading } = useHomeAmenities(hotelId);
  const { mutate: reorderAmenities } = useReorderAmenities();
  const { mutate: updateAmenity, isPending: isUpdating } = useUpdateAmenity();
  const items = data?.items ?? [];
  const states = getAmenityHomeStates(items);
  const stateById = new Map(
    items.map((amenity, index) => [amenity.id, states[index]]),
  );
  const indexById = new Map(items.map((amenity, index) => [amenity.id, index]));

  const handleReorder = (next: Amenity[]) => {
    if (!hotelId) return;
    reorderAmenities({ hotelId, ids: next.map((amenity) => amenity.id) });
  };

  const handleUnstar = (amenity: Amenity) =>
    updateAmenity({ id: amenity.id, data: { showOnHome: false } });

  const renderStateBadge = (state: AmenityHomeState) => (
    <Badge variant={state.kind === "shown" ? "new_secondary" : "new_outline"}>
      {t(
        AMENITY_HOME_STATE_KEYS[state.kind],
        state.kind === "shown" ? { position: state.position } : undefined,
      )}
    </Badge>
  );

  const renderRow = (amenity: Amenity) => {
    const starLabel = t(getHomeStarLabelKey(amenity), { name: amenity.name });
    return (
      <div
        className={cn(
          "border-base-border flex min-h-14 items-center gap-3 rounded-lg border bg-white px-3 py-2",
          access.canEdit && "cursor-grab active:cursor-grabbing",
        )}
      >
        {access.canEdit && (
          <GripVertical
            role="img"
            aria-label={t("amenities.home.dragHandle", { name: amenity.name })}
            className="size-4 shrink-0 text-black/45"
          />
        )}
        <span className="text-muted-foreground w-5 shrink-0 text-right text-sm tabular-nums">
          {(indexById.get(amenity.id) ?? 0) + 1}
        </span>
        <div className="border-base-border bg-muted flex size-10 shrink-0 items-center justify-center overflow-hidden rounded border">
          <PresignedImageWithPlaceholder
            className="size-10 object-cover"
            photo={amenity.photoId || undefined}
            alt={amenity.name}
            placeholder={<Image className="text-muted-foreground size-5" />}
          />
        </div>
        <span
          className="min-w-0 flex-1 truncate text-sm font-medium text-black/96"
          title={amenity.name}
        >
          {amenity.name}
        </span>
        {renderStateBadge(stateById.get(amenity.id) ?? { kind: "overflow" })}
        {access.canEdit && (
          <Button
            variant="new_ghost"
            size="icon_sm"
            aria-label={starLabel}
            title={starLabel}
            disabled={isUpdating}
            onPointerDown={stopInsideSortable}
            onKeyDown={stopInsideSortable}
            onClick={() => handleUnstar(amenity)}
            className="text-royal-glow-3"
          >
            <Star fill="currentColor" />
          </Button>
        )}
      </div>
    );
  };

  let content: React.ReactNode;
  if (isLoading) {
    content = (
      <div className="text-muted-foreground py-4 text-center text-sm">
        {t("amenities.home.loading")}
      </div>
    );
  } else if (!items.length) {
    content = (
      <div className="text-muted-foreground py-4 text-center text-sm">
        {t("amenities.home.empty")}
      </div>
    );
  } else if (access.canEdit) {
    content = (
      <SortableList
        items={items}
        onReorder={handleReorder}
        keyExtractor={(amenity) => amenity.id}
        direction="column"
        className="flex flex-col gap-2"
        renderItem={renderRow}
      />
    );
  } else {
    content = (
      <div className="flex flex-col gap-2">
        {items.map((amenity) => (
          <div key={amenity.id}>{renderRow(amenity)}</div>
        ))}
      </div>
    );
  }

  return (
    <section
      aria-labelledby="home-amenities-title"
      className="border-base-border flex flex-col gap-4 rounded-xl border bg-white p-4 shadow-sm"
    >
      <div className="flex flex-col gap-1">
        <h2
          id="home-amenities-title"
          className="text-base font-semibold text-black/96"
        >
          {t("amenities.home.title")}
        </h2>
        <p className="text-muted-foreground text-sm">
          {t("amenities.home.hint")}
        </p>
      </div>
      {content}
    </section>
  );
};

export default HomeAmenitiesSection;
